import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { BpTable } from '../../../../shared/components/cards/table/BpTable'
import { TTableResponse } from '../../../../shared/types/TableResponse.type'
import { ColumnsType } from 'antd/lib/table'
import { IBpPaginationProps } from '../../../../shared/components/cards/pagination/BpPagination'
import { OrganicResultsFilterInterface } from '../../model/types/OrganicResultsFilter.interface'
import {
  selectOrganicResultsFilters,
  selectOrganicResultsPagination,
  setOrganicResultsPagination,
} from '../../model/organicResultsSlice'
import {
  AdvertiserReportsOrganicItems,
  CommaLists,
  GetAdvertiserReportsOrganicApiArg,
  useGetAdvertiserReportsOrganicQuery,
} from '../../../../app/api'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { TriangleButton } from '../../../../shared/components/buttons/triangle/TriangleButton'
import { SorterResult } from 'antd/lib/table/interface'
import { useSorter } from '../../../../shared/lib/hooks/UseSorter'
import { OrganicResultsTableExpandRow } from '../organic-results-table-expand-row/OrganicResultsTableExpandRow'
import { IWithExpandedAllProps, withExpanded } from '../../../../shared/lib/hoc/WithExpanded'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { useTranslation } from 'react-i18next'
import { DomainCell } from '../../ui/cells/DomainCell'
import { HiddenActionCell } from '../../ui/cells/HiddenActionCell'
import { AvgPositionCell } from '../../../../shared/ui/cells/AvgPositionCell'
import { CustomTag } from '../../../../lib/widgets/tags-editor/TagsEditor'
import clsx from 'clsx'
import styles from './OrganicResultsTable.module.scss'
import { useMenuLayout } from '../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { OrganicResultsActions } from '../organic-results-actions/OrganicResultsActions'
import { ActionsPortal } from '../../../../shared/components/utility/actions-slot/ActionsSlot'

const Table = ({
  onToggleExpand,
  isExpandAll,
  expandedRowKeys,
  onToggleExpandAll,
}: IWithExpandedAllProps) => {
  const { t } = useTranslation()
  const { menuLayout } = useMenuLayout()
  const [countRows, setCountRows] = useState<number>(0)
  const organicResultsFilter = useAppSelector<OrganicResultsFilterInterface>(
    selectOrganicResultsFilters,
  )
  const organicResultsPagination = useAppSelector<IPagination>(selectOrganicResultsPagination)
  const {
    project,
    engine_id,
    dateRange: date,
    country,
    policy,
    site,
    keyword,
    affiliate,
    show_hidden,
    show_affiliates,
    has_brand,
    tags,
    reversed_filters,
  } = organicResultsFilter
  const { current, pageSize, total } = organicResultsPagination

  const [queryParams, setQueryParams] = useState<
    Partial<CommaLists<GetAdvertiserReportsOrganicApiArg>>
  >({
    page: current,
    'per-page': pageSize,
  })
  const { data, isLoading, isFetching } = useGetAdvertiserReportsOrganicQuery(
    queryParams as GetAdvertiserReportsOrganicApiArg,
    {
      refetchOnMountOrArgChange: tags?.length,
      skip: !queryParams.projectId || !queryParams.engineId,
    },
  )
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!project) return

    setQueryParams({
      ...queryParams,
      projectId: +project,
      engineId: +engine_id,
      countryId: country ? +country : undefined,
      policy,
      date,
      site,
      keywordId: keyword || undefined,
      affiliateId: affiliate || undefined,
      showHidden: show_hidden,
      showAffiliates: show_affiliates,
      hasBrand: has_brand,
      tagIds: tags,
      reversedFilters: (reversed_filters || undefined) as unknown as string[],
    })
  }, [organicResultsFilter])

  useEffect(() => {
    if (data?.data) setCountRows(data.data.total)
  }, [data])

  const columns: ColumnsType<AdvertiserReportsOrganicItems> = [
    {
      title: t('table.columns.domain'),
      dataIndex: 'domain',
      key: 'domain',
      render: (_: string, record) => (
        <DomainCell {...record} project={project} tags={record.tags as CustomTag[]} />
      ),
      width: '320px',
    },
    {
      title: t('table.columns.pages'),
      dataIndex: 'pages_count',
      key: 'pages_count',
      sorter: true,
    },
    {
      title: t('table.columns.keywords'),
      dataIndex: 'keywords_count',
      key: 'keywords_count',
      sorter: true,
    },
    {
      title: t('table.columns.affiliates'),
      dataIndex: 'affiliates_count',
      key: 'affiliates_count',
      sorter: true,
    },
    {
      title: t('table.columns.avgPosition'),
      dataIndex: 'position',
      key: 'position',
      render: (_: string, record) => <AvgPositionCell {...record} />,
      sorter: true,
    },
    {
      title: t('table.columns.lastFound'),
      dataIndex: 'last_found',
      key: 'last_found',
      width: 150,
      sorter: true,
    },
    {
      key: 'actions',
      render: (_: AdvertiserReportsOrganicItems, record) => (
        <HiddenActionCell {...record} project={project} />
      ),
      width: 64,
      fixed: 'right',
    },
  ]

  const handleTableChange = (
    { pageSize, current, total }: IBpPaginationProps,
    sorter?:
      | SorterResult<AdvertiserReportsOrganicItems>
      | SorterResult<AdvertiserReportsOrganicItems>[],
  ) => {
    dispatch(
      setOrganicResultsPagination({
        pageSize: pageSize as number,
        current: current as number,
        total: total as number,
      }),
    )
    setQueryParams({
      ...queryParams,
      page: current,
      'per-page': pageSize,
      sort: useSorter(sorter),
    })
  }

  const actions = (
    <OrganicResultsActions
      isExpandAll={isExpandAll}
      queryParams={queryParams}
      onToggleExpandAll={onToggleExpandAll}
    />
  )

  return (
    <section className={clsx(styles.root, { [styles.headless]: menuLayout !== 'top' })}>
      {menuLayout === 'top' ? (
        <div className={clsx(styles.header)}>
          <h3 className={clsx(styles.title)}>
            {t('pages.organicResults.organicResultsCount', { count: countRows })}{' '}
            <BpTooltip title={t('pages.organicResults.organicResultsTooltip')} />
          </h3>
          {actions}
        </div>
      ) : (
        // В боковом меню заголовка нет — действия уезжают в строку с чипами фильтров
        <ActionsPortal>{actions}</ActionsPortal>
      )}
      <BpTable
        stickyExpandedRow
        expandable={{
          expandIcon: ({ expanded, onExpand, record }) => (
            <IconButton onClick={(e) => onExpand(record, e)}>
              <TriangleButton color='#A059FF' position={expanded ? 'top' : 'down'} />
            </IconButton>
          ),
          expandedRowRender: (record: AdvertiserReportsOrganicItems) => (
            <OrganicResultsTableExpandRow {...record} />
          ),
          onExpand: (expanded, { domain }) => {
            if (onToggleExpand) onToggleExpand(expanded, `${domain}`)
          },
          expandedRowKeys: isExpandAll
            ? data?.data?.items?.map((item) => item.domain)
            : expandedRowKeys,
        }}
        pagination={{
          current,
          pageSize,
          total,
        }}
        columns={columns}
        data={data as TTableResponse<AdvertiserReportsOrganicItems>}
        dataSource={data?.data?.items}
        loading={isLoading || isFetching}
        rowClassName={(e) => (e.is_hide ? 'disabled' : '')}
        rowKey={(record) => `${record.domain}`}
        // в верхнем меню над таблицей заголовок с margin-bottom 16px, в боковом — gap колонки страницы
        stubGap={menuLayout === 'top' ? 16 : 24}
        onClickRow={(record) => {
          const isExpanded = isExpandAll ? true : expandedRowKeys?.includes(record.domain)

          onToggleExpand?.(!isExpanded, record.domain)
        }}
        onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
      />
    </section>
  )
}

export const OrganicResultsTable = withExpanded(Table)
